import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { api } from "@/lib/api-client"
import { queryKeys } from "@/lib/query-keys"
import { useParserUploadStore } from "@/stores/parser-upload-store"

import { useAIParserSession } from "./use-ai-parser-session"

type Data = {
  sessionId: string | null
  files: File[]
}

type UploadResult = {
  session_id: string | null
  status: "success" | "partial_success" | "failed"
  uploaded: { filename: string; uploaded_at: string }[]
  failed: { filename: string; message: string }[]
}

function uploadDocuments(data: Data): Promise<UploadResult> {
  const formData = new FormData()
  formData.append("session_id", data.sessionId ?? "")

  data.files.forEach((file) => {
    formData.append("files", file)
  })

  return api.post("/parser/upload-documents", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
}

export function useUploadDocumentsMutation() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setCurrentSessionId, sessionType } = useAIParserSession()

  const startUpload = useParserUploadStore((state) => state.startUpload)
  const uploadComplete = useParserUploadStore((state) => state.uploadComplete)
  const uploadFailed = useParserUploadStore((state) => state.uploadFailed)

  return useMutation({
    mutationFn: uploadDocuments,
    onMutate: (variables) => {
      const targetSessionId = variables.sessionId ?? "new"
      startUpload(targetSessionId, variables.files.length)
    },
    onSuccess: async (data) => {
      if (data.status !== "failed" && data.session_id) {
        setCurrentSessionId(data.session_id)
        await queryClient.invalidateQueries({
          queryKey: queryKeys.fullSession(data.session_id),
        })
      }
      await queryClient.invalidateQueries({
        queryKey: queryKeys.sessionHistory(sessionType),
      })
      uploadComplete()

      if (data.status === "failed" && data.failed.length) {
        const failedNames = data.failed.map((f) => f.filename).join(", ")
        toast.error(
          t("pages.ai_parser.toasts.upload_all_failed", {
            defaultValue: "All files failed to upload",
          }),
          { description: failedNames }
        )
      } else if (data.status === "partial_success" && data.failed.length) {
        const failedNames = data.failed.map((f) => f.filename).join(", ")
        toast.warning(
          t("pages.ai_parser.toasts.upload_partial", {
            defaultValue: "Some files failed to upload",
          }),
          { description: failedNames }
        )
      } else {
        toast.success(t("pages.ai_parser.toasts.documents_uploaded"))
      }

      if (data.status !== "failed" && data.session_id) {
        void navigate(`/ai-parser/chat/${data.session_id}`)
      }
    },
    onError: (error: Error & { response?: { data?: UploadResult; status?: number } }) => {
      uploadFailed()

      // Handle structured 422 response (all files failed but server returned details)
      const errorData = error.response?.data
      if (
        error.response?.status === 422 &&
        errorData?.status === "failed" &&
        errorData.failed.length
      ) {
        const failedNames = errorData.failed.map((f) => f.filename).join(", ")
        toast.error(
          t("pages.ai_parser.toasts.upload_all_failed", {
            defaultValue: "All files failed to upload",
          }),
          { description: failedNames }
        )
        return
      }

      toast.error(
        t("pages.ai_parser.toasts.upload_failed", {
          defaultValue: "Failed to upload documents. Please try again.",
        }),
        { description: error.message }
      )
    },
  })
}
